import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CMSService from '../../services/CMSService';
import { SocialToolboxLink } from '../../types';

/**
 * Social Toolbox Component
 * Fixed floating button for social interaction
 */

const DEFAULT_SOCIAL_LINKS: SocialToolboxLink[] = [
  { id: 'facebook', icon: 'facebook-f', color: 'bg-blue-600 hover:bg-blue-700', href: 'https://facebook.com/jennifernguyen.an', label: 'Facebook' },
  { id: 'youtube', icon: 'youtube', color: 'bg-red-600 hover:bg-red-700', href: 'https://youtube.com/@AnhNguJenniferNguyenAn', label: 'YouTube' },
  { id: 'tiktok', icon: 'tiktok', color: 'bg-black hover:bg-gray-900', href: 'https://tiktok.com', label: 'TikTok' },
  { id: 'gmail', icon: 'envelope', color: 'bg-red-500 hover:bg-red-600', href: 'mailto:contact@example.com', label: 'Email' },
];

const SocialToolbox: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [socialLinks, setSocialLinks] = useState<SocialToolboxLink[]>(DEFAULT_SOCIAL_LINKS);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    CMSService.getSocialToolboxConfig().then((res) => {
      if (res.statusCode === 200 && res.data?.links?.length) {
        setSocialLinks(res.data.links);
      }
    }).catch(() => {/* keep defaults */});
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !(event.target as HTMLElement).closest('.social-toolbox-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, isMobile]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
    },
  };

  return (
    <div className="social-toolbox-container fixed bottom-4 right-4 z-50">
      <div className={`relative ${!isMobile ? 'group' : ''}`}>
        {/* Main Button */}
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Social media links"
          aria-expanded={isOpen}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center justify-center w-12 h-12 rounded-full bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white/90 dark:bg-white/20 dark:text-gray-300 transition-colors shadow-lg border border-emerald-200 dark:border-emerald-900/30 z-10 relative"
        >
          <motion.i
            className={`fas ${isOpen ? 'fa-times' : 'fa-share-alt'} text-sm transition-transform duration-300`}
            animate={{ rotate: isOpen ? 90 : 0 }}
          />
        </motion.button>

        {/* Social Links */}
        <motion.div
          className={`
            absolute bottom-14 right-1 flex flex-col-reverse gap-3 items-center transition-all duration-300 ease-in-out
            ${
              isMobile
                ? isOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible translate-y-2'
                : isOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0'
            }
          `}
          variants={containerVariants}
          initial="hidden"
          animate={isOpen || !isMobile ? 'visible' : 'hidden'}
        >
          {socialLinks.map((social) => (
            <motion.div
              key={social.id}
              variants={itemVariants}
              className="transform transition-transform hover:scale-110 hover:-translate-x-1"
            >
              <a
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit our ${social.label} page`}
                className={`flex items-center justify-center w-10 h-10 rounded-full ${social.color} text-white transition-colors shadow-md`}
              >
                <i className={`fab fa-${social.icon} text-sm`}></i>
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default SocialToolbox;
